import type {
  PlaybookCriterionNode,
  PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { CriterionGrade } from "@repo/app-types/entities/recording";
import {
  buildTeammateDisplayNames,
  type BasicTeammate,
} from "@repo/app-types/entities/teammate";
import {
  getGradingDisplayNames,
  type GradingId,
  type TeammateAccountGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Link } from "@repo/ui/Link/index";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconCopy, IconLink } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { appPaths } from "../../app/appPaths.js";
import { buildAppUrl } from "../../helpers/appUrls.js";
import { copyRichText } from "../../helpers/utilities.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { renderGradingValue } from "./gradingHelpers.js";

interface ShareGradingModalProps {
  isOpen: boolean;
  onClose: () => void;
  grading: TeammateAccountGrading;
  criterion: PlaybookCriterionNode;
  grade?: CriterionGrade;
}

export const ShareGradingModal: FC<ShareGradingModalProps> = ({
  isOpen,
  onClose,
  grading,
  criterion,
  grade,
}) => {
  // teammate is derived from the grading for the display name only.
  const { data: teammate } = useBasicTeammateQuery(grading.teammate_id);

  // Create the share link
  const { primaryDisplayName } = getGradingDisplayNames(grading);
  const link = getShareLinkForGrading({
    playbookId: grading.playbook_id,
    gradingId: grading.id,
    criterionId: criterion.id,
  });

  // Create the share text snippet
  const shareSnippet = generateShareSnippet(
    primaryDisplayName,
    criterion,
    grade?.value,
    grade?.commentary,
    link,
    teammate,
    grading.last_recorded_at,
  );

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader onClose={onClose}>Share analysis</ModalHeader>

      <div
        className={twMerge(
          "mt-4 flex w-full cursor-text space-x-2 rounded-md border border-solid border-border-strong bg-slate-50 px-2 py-2 text-xs hover:bg-slate-100",
          PH_MASK_CLASS,
        )}
      >
        {shareSnippet.jsx}
      </div>

      <div className="mt-2 text-sm text-muted-foreground">
        <span>Paste this snippet into Slack, Teams, or emails to share.</span>
      </div>

      <div className="mt-4 flex items-center space-x-4">
        <Button
          variant={ButtonVariant.Primary}
          onClick={async () => {
            await copyRichText(shareSnippet.richText, shareSnippet.plainText);

            showToast({
              title: `Share text copied`,
              variant: ToastVariant.Success,
            });
          }}
          icon={<IconCopy className="h-4 w-4" />}
        >
          Copy text
        </Button>
        <Button
          variant={ButtonVariant.Secondary}
          onClick={async () => {
            await navigator.clipboard.writeText(link);

            showToast({
              title: `Link copied`,
              variant: ToastVariant.Success,
            });
          }}
          icon={<IconLink className="h-4 w-4" />}
        >
          Copy link
        </Button>
      </div>
    </Modal>
  );
};

// Helper function to generate share link for grading assessment
function getShareLinkForGrading({
  playbookId,
  gradingId,
  criterionId,
}: {
  playbookId: PlaybookId;
  gradingId: GradingId;
  criterionId: string;
}) {
  return buildAppUrl(
    appPaths.playbookCallCriterion.build({
      playbookId,
      gradingId,
      criterionId,
    }),
  );
}

// Generates share snippet in JSX, plaintext, and richtext formats.
function generateShareSnippet(
  primaryDisplayName: string | undefined,
  criterion: PlaybookCriterionNode,
  grade: string | undefined,
  commentary: string | undefined,
  link: string,
  teammate: BasicTeammate | undefined,
  lastRecordedAt: string,
) {
  const linkElement = <Link href={link}>See the full analysis</Link>;

  // Format teammate name
  const teammateName = teammate
    ? buildTeammateDisplayNames(teammate).primaryDisplayName
    : "Unknown";

  // Format date
  const recordedDate = DateTime.fromISO(lastRecordedAt).toFormat("MMM d, yyyy");

  const gradeDisplay = renderGradingValue(criterion.grading_value_type, grade, {
    plaintext: true,
  });

  // Create the new format: Grading analysis for [teammate name] / Customer: [name] ([date]) / [criteria]: [value] / [Commentary] / See the full analysis
  const plainTextContent = [
    `Grading analysis for ${teammateName}`,
    `Customer: ${primaryDisplayName || "Unknown"} (${recordedDate})`,
    `${criterion.name}: ${gradeDisplay}`,
    commentary && commentary.trim() ? commentary : null,
    "",
    link,
  ]
    .filter(Boolean)
    .join("\n");

  // Build rich text HTML safely using DOM APIs
  // TODO: Dedupe with JSX below?
  const container = document.createElement("div");

  const titleStrong = document.createElement("strong");
  titleStrong.textContent = `Grading analysis for ${teammateName}`;
  container.appendChild(titleStrong);
  container.appendChild(document.createElement("br"));

  const customerStrong = document.createElement("strong");
  customerStrong.textContent = "Customer:";
  container.appendChild(customerStrong);
  container.appendChild(
    document.createTextNode(` ${primaryDisplayName} (${recordedDate})`),
  );
  container.appendChild(document.createElement("br"));

  const criterionStrong = document.createElement("strong");
  criterionStrong.textContent = `${criterion.name}:`;
  container.appendChild(criterionStrong);
  container.appendChild(document.createTextNode(` ${gradeDisplay}`));
  container.appendChild(document.createElement("br"));

  if (commentary && commentary.trim()) {
    container.appendChild(document.createTextNode(commentary));
    container.appendChild(document.createElement("br"));
  }

  const anchor = document.createElement("a");
  anchor.href = link;
  anchor.textContent = "See the full analysis";
  container.appendChild(anchor);

  const richTextContent = container.outerHTML;

  return {
    plainText: plainTextContent,
    richText: richTextContent,
    jsx: (
      <div>
        <strong>Grading analysis for {teammateName}</strong>
        <br />
        <strong>Customer:</strong> {primaryDisplayName} ({recordedDate})
        <br />
        <strong>{criterion.name}:</strong> {gradeDisplay}
        <br />
        {commentary && commentary.trim() && (
          <>
            {commentary}
            <br />
          </>
        )}
        {linkElement}
      </div>
    ),
  };
}
